import React, { useCallback, useMemo } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Card } from '@altermaind/web/ui';
import { useDialog } from '@/_shared/hooks/useDialog';
import { getContentDetailsCall } from '@/training/hardSkills/state/api/getContentDetailsCall';
import { DialogStartQuizPayload } from '@/training/quiz/components/DialogStartQuiz/types';
import { CardsContent } from '../CardsContent';
import { CardsHeader } from '../CardsHeader';

type BaseProps = {
  id: string;
  questions?: number;
  category?: string;
  title?: string;
  description?: string;
  inProgress?: boolean;
  halfHeightLayout?: boolean;
  isFavorite?: boolean;
  isCompleted?: boolean;
  isSkillToBoost?: boolean;
  currentStep?: number;
  totalSteps?: number;
};

type Props<Loading extends boolean = boolean> = Loading extends true
  ? Partial<BaseProps> & { loading: true }
  : BaseProps & { loading?: false };

export const Quiz = ({
  id,
  questions,
  category,
  title,
  description,
  loading = false,
  inProgress = false,
  isFavorite = false,
  isCompleted = false,
  halfHeightLayout,
  isSkillToBoost = false,
  currentStep,
  totalSteps,
}: Props) => {
  const t = useTranslations('trainingHardSkills.cards.QUIZ');
  const { loading: loadingGetContentDetails } = getContentDetailsCall.hook();
  const { openDialog: openDialogQuizStarter } = useDialog<DialogStartQuizPayload>('quizStarter');

  const onQuizStart = useCallback(() => {
    if (id) {
      openDialogQuizStarter({
        quizId: id,
        isSkillToBoost,
      });
    }
  }, [id, isSkillToBoost, openDialogQuizStarter]);

  const textButton = useMemo(() => {
    if (inProgress) {
      return t('resume');
    }
    if (isCompleted) {
      return t('restart');
    }
    return t('start');
  }, [inProgress, isCompleted, t]);

  const iconButton = useMemo(() => {
    if (inProgress) {
      return 'emptyPlay';
    }
    if (isCompleted) {
      return 'refresh';
    }
    return 'emptyPlay';
  }, [inProgress, isCompleted]);

  const progress = useMemo(() => {
    if (!totalSteps || !currentStep) {
      return undefined;
    }
    const current = currentStep;
    const label = t('progress', {
      current,
      total: totalSteps,
    });
    return { label, current, total: totalSteps };
  }, [totalSteps, currentStep, t]);

  return (
    <Card
      width='100%'
      padding='none'
      justifyContent='space-between'
      display='flex'
      flexDirection={halfHeightLayout ? 'row' : 'column'}
      height='100%'
    >
      <CardsHeader
        contentId={id as string}
        mainInfoContent={
          questions ? { text: t('questions', { questions }), iconName: 'document' } : undefined
        }
        imageAssetName='doubleBoxQuestions'
        loading={loading}
        halfHeightLayout={halfHeightLayout}
        isFavorite={isFavorite}
        inProgress={inProgress}
        isSkillToBoost={isSkillToBoost}
        hideFavoriteButton
      />
      <CardsContent
        button={{
          iconName: iconButton,
          text: textButton,
          onClick: onQuizStart,
          loading: loadingGetContentDetails,
        }}
        category={category}
        title={title}
        description={description || ''}
        loading={loading}
        mainInfoContent={
          questions ? { text: t('questions', { questions }), iconName: 'document' } : undefined
        }
        progressBar={inProgress ? progress : undefined}
        halfHeightLayout={halfHeightLayout}
      />
    </Card>
  );
};
